// Utilidad para limpiar palabras prohibidas y sanitizar textos antes de publicar
import { prohibitedWordsES } from './prohibitedWords.es';

export const defaultProhibitedWords = prohibitedWordsES;

export function getProhibitedWords() {
  try {
    const stored = localStorage.getItem('ml_prohibited_words');
    if (stored) {
      // Primero intentamos parsear como JSON
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      } catch { /* si no es JSON, seguimos abajo */ }
      // Si no es JSON, permitir texto plano (pegado) separado por comas, saltos de línea, tabs o pipes
      const parts = String(stored)
        .split(/[\n\r\t,;|]+/g)
        .map(s => s.trim())
        .filter(Boolean);
      if (parts.length) {
        // Quitar duplicados respetando el orden
        const seen = new Set();
        const arr = [];
        for (const p of parts) {
          if (!seen.has(p)) { seen.add(p); arr.push(p); }
        }
        return arr;
      }
    }
  } catch { /* ignore */ }
  return defaultProhibitedWords;
}

export function sanitizeText(text, options = {}) {
  if (!text || typeof text !== 'string') return { text: text || '', found: [] };

  const words = options?.words || getProhibitedWords();
  if (!Array.isArray(words) || words.length === 0) return { text, found: [] };

  let sanitized = text;
  const found = [];
  for (const raw of words) {
    if (!raw || typeof raw !== 'string') continue;
    const pattern = new RegExp(`\\b${escapeRegExp(raw)}\\b`, 'gi');
    if (pattern.test(sanitized)) {
      found.push(raw);
      sanitized = sanitized.replace(pattern, options?.replacement ?? '');
    }
  }
  // Colapsar espacios múltiples causados por reemplazos
  sanitized = sanitized.replace(/\s{2,}/g, ' ').trim();
  return { text: sanitized, found };
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Convierte texto y devuelve también si hubo cambios
export function sanitizeFields(product, fields = ['title', 'titulo', 'description', 'descripcion'], options = {}) {
  const out = { product: { ...product }, totalFound: 0, hits: {} };
  for (const f of fields) {
    if (typeof out.product[f] !== 'string') continue;
    const { text, found } = sanitizeText(out.product[f], options);
    out.product[f] = text;
    if (found.length) {
      out.totalFound += found.length;
      found.forEach(w => { out.hits[w] = (out.hits[w] || 0) + 1; });
    }
  }
  return out;
}

// Sanitiza recursivamente todas las propiedades string del objeto (excepto URLs) usando la lista de palabras prohibidas
export function sanitizeObjectStrings(input, options = {}) {
  const words = options?.words || getProhibitedWords();
  if (!input || typeof input !== 'object') return input;

  const isUrl = (s) => typeof s === 'string' && /^https?:\/\//i.test(s);
  const walk = (val) => {
    if (typeof val === 'string') {
      if (isUrl(val)) return val; // no tocar URLs
      return sanitizeText(val, { words, replacement: options?.replacement ?? '' }).text;
    }
    if (Array.isArray(val)) return val.map(walk);
    if (val && typeof val === 'object') {
      const out = Array.isArray(val) ? [] : {};
      for (const [k, v] of Object.entries(val)) {
        out[k] = walk(v);
      }
      return out;
    }
    return val;
  };

  return walk(input);
}

// ---------------- Filtrado por palabras prohibidas (para omitir productos al cargar) ----------------

// Normaliza texto para comparación: minúsculas, sin tildes y sin caracteres extraños
export function normalizeForMatch(str) {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // quitar diacríticos
    .replace(/[®©™]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Construye índice normalizado y único de términos prohibidos
export function buildProhibitedIndex(words) {
  const arr = Array.isArray(words) ? words : [];
  const set = new Set();
  for (let w of arr) {
    if (!w || typeof w !== 'string') continue;
    // limpiar bullets y sufijos frecuentes como "New"
    w = w.replace(/^[-•|'`·]+/g, '').replace(/\s*new$/i, '').replace(/\s+/g, ' ').trim();
    const n = normalizeForMatch(w);
    if (n && n.length >= 2) set.add(n);
  }
  return Array.from(set);
}

// Verifica si un texto contiene algún término prohibido (como subcadena)
export function textHasProhibited(text, normalizedTerms) {
  if (!text || !Array.isArray(normalizedTerms) || normalizedTerms.length === 0) return { has: false };
  const t = normalizeForMatch(text);
  if (!t) return { has: false };
  for (const term of normalizedTerms) {
    if (!term) continue;
    if (t.includes(term)) return { has: true, match: term };
  }
  return { has: false };
}

// Revisa todas las propiedades string del producto para detectar términos prohibidos
export function productHasProhibited(product, normalizedTerms) {
  const matches = new Set();
  if (!product || !normalizedTerms?.length) return { has: false, matches: [] };
  for (const [key, val] of Object.entries(product)) {
    if (typeof val !== 'string') continue;
    const res = textHasProhibited(val, normalizedTerms);
    if (res.has) {
      matches.add(res.match);
      // opcional: seguir buscando para listar más coincidencias rápidas
      if (matches.size >= 5) break; // limitar para rendimiento
    }
  }
  return { has: matches.size > 0, matches: Array.from(matches) };
}

// Detección y conversión de USD a COP con heurística segura
export function convertUsdToCopIfNeeded(rawPrice, parsedPrice, rate) {
  const validRate = Number(rate);
  if (!Number.isFinite(validRate) || validRate <= 0) {
    throw new Error('No existe una tasa USD→COP válida para convertir el precio');
  }

  // Si el texto contiene USD/US$ asumimos dólares
  const rawStr = typeof rawPrice === 'string' ? rawPrice.toLowerCase() : '';
  const explicitUsd = /usd|us\$|u\$\s?d/.test(rawStr);

  // Si no hay pista textual, usar heurística por magnitud (valores bajos suelen ser USD)
  const looksUsdByMagnitude = !explicitUsd && typeof parsedPrice === 'number' && isFinite(parsedPrice) && parsedPrice > 0 && parsedPrice < 2000;

  if (explicitUsd || looksUsdByMagnitude) {
    const cop = Math.round((parsedPrice || 0) * validRate);
    return { converted: true, value: cop };
  }
  return { converted: false, value: parsedPrice };
}

// ---- Detección y normalización de idioma a Español (heurística ligera) ----

const ES_STOPWORDS = ['el','la','los','las','de','del','y','en','para','con','sin','por','un','una','uno','sobre','más','menos','nuevo','usado','color','tamaño','modelo','marca','incluye','envío','gratis','garantía'];
const EN_STOPWORDS = ['the','and','for','with','without','by','in','on','from','to','of','more','less','new','used','color','size','model','brand','includes','shipping','free','warranty'];

export function detectLanguage(text) {
  if (!text || typeof text !== 'string') return 'unknown';
  const t = text.toLowerCase();
  // Señales fuertes de español
  if (/[ñáéíóúü]/.test(t)) return 'es';
  // Recuento de stopwords
  const count = (arr) => arr.reduce((acc,w)=> acc + (t.includes(` ${w} `) ? 1 : 0), 0);
  const esScore = count(ES_STOPWORDS);
  const enScore = count(EN_STOPWORDS);
  if (esScore === 0 && enScore === 0) return 'unknown';
  return esScore >= enScore ? 'es' : 'en';
}

const SIMPLE_DICTIONARY = [
  // Frases comunes primero
  [/free shipping/gi, 'envío gratis'],
  [/brand new/gi, 'nuevo'],
  [/money back guarantee/gi, 'garantía de devolución de dinero'],
  [/buy now/gi, 'compra ahora'],
  [/limited time offer/gi, 'oferta por tiempo limitado'],
  [/high quality/gi, 'alta calidad'],
  [/in stock/gi, 'en stock'],
  [/on sale/gi, 'en oferta'],
  [/fast delivery/gi, 'entrega rápida'],
  // Palabras sueltas
  [/new/gi, 'nuevo'],
  [/used/gi, 'usado'],
  [/color/gi, 'color'],
  [/size/gi, 'tamaño'],
  [/model/gi, 'modelo'],
  [/brand/gi, 'marca'],
  [/features/gi, 'características'],
  [/description/gi, 'descripción'],
  [/includes/gi, 'incluye'],
  [/package includes/gi, 'el paquete incluye'],
  [/warranty/gi, 'garantía'],
  [/shipping/gi, 'envío'],
  [/free/gi, 'gratis'],
  [/discount/gi, 'descuento'],
  [/sale/gi, 'oferta'],
  [/material/gi, 'material'],
  [/battery/gi, 'batería'],
  [/wireless/gi, 'inalámbrico'],
  [/waterproof/gi, 'resistente al agua'],
  [/smart/gi, 'inteligente'],
  [/compatible/gi, 'compatible'],
  // Unidades
  [/(\d+)\s?inch(es)?/gi, '$1 pulgadas'],
  [/(\d+)\s?in\b/gi, '$1 in'],
  [/(\d+)\s?cm\b/gi, '$1 cm']
];

export async function translateTextToSpanish(text, options = {}) {
  if (!text || typeof text !== 'string') return text || '';
  const lang = detectLanguage(text);
  if (lang === 'es') return text; // ya está en español

  const mode = options?.mode || (localStorage.getItem('ml_translation_mode') || 'simple');
  // Opción API: si se configura VITE_TRANSLATE_API_URL, se intenta traducir por API
  if (mode === 'api') {
    try {
      const apiUrl = options?.apiUrl || import.meta.env?.VITE_TRANSLATE_API_URL;
      if (apiUrl) {
        const res = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ q: text, source: lang === 'unknown' ? 'auto' : lang, target: 'es' })
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.translatedText) return data.translatedText;
        }
      }
    } catch { /* si falla, caer al modo simple */ }
  }
  // Modo simple: reemplazos básicos para ecommerce
  let out = text;
  for (const [pattern, replacement] of SIMPLE_DICTIONARY) {
    out = out.replace(pattern, replacement);
  }
  return out;
}

export async function ensureSpanishFields(product, fields = ['title','titulo','description','descripcion'], options = {}) {
  const out = { ...product };
  for (const f of fields) {
    if (typeof out[f] === 'string' && out[f].trim()) {
      const translated = await translateTextToSpanish(out[f], options);
      out[f] = translated;
    }
  }
  return out;
}
